/**
 * 项目工作空间布局（备用）：左侧 WorkspaceSider 为当前项目子菜单，顶栏与 MainLayout 共用 AppHeader / PageTabs / SearchModal。
 * 路由：`/medproject/projects/:projectId/*`，与 MainLayout 平级注册。返回列表见 workspaceConfig.WORKSPACE_PARENT_PATH。
 */

import { Button, Card, Drawer, Layout, Menu, Result, theme } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppBreakpoint } from '@/contexts/BreakpointContext';
import { ProjectContextProvider } from '@/contexts/ProjectContext';
import { rememberWorkspaceProjectTabTitle } from '@/hooks/workspaceTabProjectTitleCache';
import { useSyncedPageTabs } from '@/hooks/useSyncedPageTabs';
import useMedProjectActivitiesModuleId from '@/hooks/useMedProjectActivitiesModuleId';
import { getWorkspaceProjectById } from '@/services/workspaceProjectLookup';
import { showMedProject } from '@services/MedProject';
import { UserWatermark } from '@/hooks/useWatermark';
import AppHeader from './AppHeader';
import KeepAliveOutlet from './KeepAliveOutlet';
import { buildMedProjectWorkspaceMenuEntries } from './data/projectMenuItems';
import PageTabs from './PageTabs';
import SearchModal from './SearchModal';
import { useEnableTableScrollYLayout } from './tableScrollLayout';
import { WORKSPACE_PARENT_PATH } from './workspaceConfig';
import WorkspaceSider from './WorkspaceSider';
import { isWorkspaceFixedTableScrollPath } from './workspaceFixedTableScrollPaths';

const { Content } = Layout;

const LS_WORKSPACE_COLLAPSED = 'workspace_sider_collapsed';

const workspaceLayoutBranchNoShrinkStyle = { flexShrink: 0 };

const workspaceLayoutRightColumnStyle = {
    minWidth: 0,
    minHeight: 0,
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
};

function safeLsSet(key, value) {
    try {
        localStorage.setItem(key, value);
    } catch {
        // quota exceeded or private-browsing mode
    }
}

export default function WorkspaceLayout() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { token } = theme.useToken();
    const { isMobile } = useAppBreakpoint();
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const useFixedTableScrollChain = enableTableScrollYLayout && isWorkspaceFixedTableScrollPath(pathname);
    const mainContainerClassName = useFixedTableScrollChain ? 'main-container' : 'main-container main-container--natural-scroll';

    const { tabs, onTabsChange } = useSyncedPageTabs();

    const [collapsed, setCollapsed] = useState(() => localStorage.getItem(LS_WORKSPACE_COLLAPSED) === 'true');
    const [searchOpen, setSearchOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const prevIsMobileRef = useRef(null);

    const openSearchModal = useCallback(() => setSearchOpen(true), []);
    const closeSearchModal = useCallback(() => setSearchOpen(false), []);

    const handleToggleSider = useCallback(() => {
        if (isMobile) {
            setMobileMenuOpen(true);
            return;
        }
        setCollapsed(prev => {
            const next = !prev;
            safeLsSet(LS_WORKSPACE_COLLAPSED, String(next));
            return next;
        });
    }, [isMobile]);

    const goParentList = useCallback(() => {
        navigate(WORKSPACE_PARENT_PATH);
    }, [navigate]);

    useEffect(() => {
        const handler = e => {
            if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                openSearchModal();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [openSearchModal]);

    useEffect(() => {
        const prev = prevIsMobileRef.current;
        if (prev === null) {
            if (isMobile) setCollapsed(true);
        } else if (prev !== isMobile) {
            if (isMobile) {
                setCollapsed(true);
            } else {
                setCollapsed(localStorage.getItem(LS_WORKSPACE_COLLAPSED) === 'true');
                setMobileMenuOpen(false);
            }
        }
        prevIsMobileRef.current = isMobile;
    }, [isMobile]);

    const activitiesModuleId = useMedProjectActivitiesModuleId(projectId);
    const isCreateMode = projectId === 'new';
    const workspaceMenuEntries = useMemo(() => {
        if (!projectId) return [];
        return buildMedProjectWorkspaceMenuEntries(projectId, activitiesModuleId, { isCreateMode });
    }, [projectId, activitiesModuleId, isCreateMode]);

    const mobileMenuItems = workspaceMenuEntries.map(row => ({
        key: row.key,
        label: row.label,
        disabled: row.disabled,
    }));

    const handleMobileMenuClick = useCallback(
        ({ key }) => {
            navigate(key);
            setMobileMenuOpen(false);
        },
        [navigate],
    );

    /** showMedProject 成功后覆盖上下文中的名称等，避免面包屑长期显示「项目 {id}」占位 */
    const [workspaceProjectDetail, setWorkspaceProjectDetail] = useState(null);

    useEffect(() => {
        if (!projectId || isCreateMode) return;
        setWorkspaceProjectDetail(null);
        let cancelled = false;
        showMedProject(projectId).then(res => {
            if (cancelled) return;
            if (res?.status_code !== 200 || !res?.data) {
                return;
            }
            const d = res.data;
            const name = typeof d.name === 'string' ? d.name.trim() : '';
            if (name) {
                rememberWorkspaceProjectTabTitle(projectId, name);
            }
            setWorkspaceProjectDetail({
                name: name || null,
                status: d.status != null ? String(d.status) : null,
                type: d.type != null ? String(d.type) : null,
            });
        });
        return () => {
            cancelled = true;
        };
    }, [projectId, isCreateMode]);

    if (!projectId) {
        return (
            <div style={{ padding: 24 }}>
                <Result
                    status="404"
                    title="未找到该项目"
                    subTitle="路由缺少项目 ID"
                    extra={
                        <Button type="primary" onClick={goParentList}>
                            返回医项目列表
                        </Button>
                    }
                />
            </div>
        );
    }

    const project = isCreateMode ? { id: 'new', name: '新增项目', status: null, type: null } : getWorkspaceProjectById(projectId);
    if (!project) {
        return (
            <div style={{ padding: 24 }}>
                <Result
                    status="404"
                    title="未找到该项目"
                    subTitle="请从医项目列表进入或检查接口数据"
                    extra={
                        <Button type="primary" onClick={goParentList}>
                            返回医项目列表
                        </Button>
                    }
                />
            </div>
        );
    }

    const contextValue = {
        id: project.id,
        name: workspaceProjectDetail?.name || project.name,
        status: workspaceProjectDetail?.status ?? project.status,
        type: workspaceProjectDetail?.type ?? project.type,
    };

    const workspaceCardChainStyle = useFixedTableScrollChain
        ? {
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
          }
        : {
              flex: 'none',
              minHeight: '100%',
              alignSelf: 'stretch',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
          };

    const workspaceCardBodyChainStyle = useFixedTableScrollChain
        ? {
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: '100%',
          }
        : {
              flex: 'none',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'visible',
              minHeight: '100%',
          };

    return (
        <Layout style={{ minHeight: '100vh' }}>
            {!isMobile && <WorkspaceSider collapsed={collapsed} menuEntries={workspaceMenuEntries} />}
            <Layout style={workspaceLayoutRightColumnStyle}>
                <div style={workspaceLayoutBranchNoShrinkStyle}>
                    <AppHeader siderCollapsed={collapsed} onToggleSider={handleToggleSider} onOpenSearch={openSearchModal} />
                </div>
                {!isMobile && (
                    <div style={workspaceLayoutBranchNoShrinkStyle}>
                        <PageTabs tabs={tabs} onTabsChange={onTabsChange} />
                    </div>
                )}
                <Content
                    component="main"
                    className={mainContainerClassName}
                    style={{
                        margin: token.margin,
                        padding: 0,
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'column',
                        minWidth: 0,
                        minHeight: 0,
                        background: token.colorBgLayout,
                    }}
                >
                    <UserWatermark>
                        <ProjectContextProvider value={contextValue}>
                            <Card
                                className="main-container-card"
                                data-table-scroll-layout={useFixedTableScrollChain ? 'fixed' : 'natural'}
                                style={workspaceCardChainStyle}
                                styles={{
                                    body: workspaceCardBodyChainStyle,
                                }}
                            >
                                <KeepAliveOutlet aliveKeys={tabs.map(t => t.key)} />
                            </Card>
                        </ProjectContextProvider>
                    </UserWatermark>
                </Content>
            </Layout>
            <SearchModal open={searchOpen} onClose={closeSearchModal} />
            {isMobile && (
                <Drawer
                    placement="left"
                    open={mobileMenuOpen}
                    onClose={() => setMobileMenuOpen(false)}
                    size={240}
                    closable={false}
                    styles={{
                        header: { display: 'none' },
                        body: { padding: 0, display: 'flex', flexDirection: 'column' },
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            height: 56,
                            padding: '0 20px',
                            flexShrink: 0,
                            borderBottom: `1px solid ${token.colorSplit}`,
                        }}
                    >
                        <img src="/logo.svg" alt="logo" style={{ width: 26, height: 26 }} />
                        <span style={{ fontWeight: 700, fontSize: 18, color: token.colorTextHeading }}>华媒康讯•智管平台</span>
                    </div>
                    <div style={{ padding: '8px 12px', flexShrink: 0, borderBottom: `1px solid ${token.colorSplit}` }}>
                        <Button
                            type="text"
                            size="small"
                            icon={<i className="bi bi-arrow-left-circle-fill" aria-hidden style={{ color: token.colorTextTertiary, opacity: 0.75 }} />}
                            onClick={() => {
                                setMobileMenuOpen(false);
                                navigate(WORKSPACE_PARENT_PATH);
                            }}
                            style={{ color: token.colorText, width: '100%', textAlign: 'left' }}
                            aria-label="返回医项目列表"
                        >
                            返回医项目列表
                        </Button>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                        <Menu mode="inline" items={mobileMenuItems} selectedKeys={[pathname]} onClick={handleMobileMenuClick} style={{ borderRight: 'none', fontSize: 16 }} />
                    </div>
                </Drawer>
            )}
        </Layout>
    );
}
